import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nativeSelectClass } from "@/tenax/tasks/taskData";
import { useRacketCommand, useVariants, variantLabel } from "./racketData";

// A new test racket from a racket taken off sale: the CRM gives the code
// (RT-0001...) and records the goods out "turned into a test racket". A
// model not in the catalog is written by hand.
export const NewRacketDialog = ({ onClose }: { onClose: () => void }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const run = useRacketCommand();
  const { data: variants = [] } = useVariants();
  const [variantId, setVariantId] = useState("");
  const [model, setModel] = useState("");
  const [condition, setCondition] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    const result = await run(
      "tenax_create_test_racket",
      {
        p_variant_id: variantId ? Number(variantId) : null,
        p_model: model.trim() || null,
        p_condition: condition.trim() || null,
      },
      "tenax.rackets.created",
    );
    setSaving(false);
    if (result) {
      onClose();
      navigate(`/test_rackets/${Number(result.data)}/show`);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("tenax.rackets.new")}</DialogTitle>
          <DialogDescription>
            {translate("tenax.rackets.new_hint")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-racket-variant">
              {translate("tenax.rackets.fields.variant_id")}
            </Label>
            <select
              id="new-racket-variant"
              className={nativeSelectClass}
              value={variantId}
              onChange={(event) => setVariantId(event.target.value)}
            >
              <option value="">—</option>
              {variants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variantLabel(variant)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-racket-model">
              {translate("tenax.rackets.fields.model_free")}
            </Label>
            <Input
              id="new-racket-model"
              value={model}
              onChange={(event) => setModel(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-racket-condition">
              {translate("tenax.rackets.fields.condition")}
            </Label>
            <Input
              id="new-racket-condition"
              value={condition}
              onChange={(event) => setCondition(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            disabled={saving || (!variantId && !model.trim())}
            onClick={create}
          >
            {translate("ra.action.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
